import { useCallback, useEffect, useState } from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { TablePagination } from '@/components/ui/TablePagination';
import { useClientPaginatedRows } from '@/hooks/usePagination';
import { financeApi } from '@/api/finance.api';
import { showApiError } from '@/utils/apiError';
import type { FinBudget, BudgetVarianceRow } from '@/types/finance';
import { notify } from '@/core/toast/notify';

export function FinanceBudgetsPage() {
  const year = new Date().getFullYear();
  const [budgets, setBudgets] = useState<FinBudget[]>([]);
  const [variance, setVariance] = useState<BudgetVarianceRow[]>([]);
  const [form, setForm] = useState({ budgetCode: '', budgetName: '', fiscalYear: year });
  const { rows: paginatedBudgets, tablePaginationProps: budgetsPaginationProps } = useClientPaginatedRows(budgets);
  const { rows: paginatedVariance, tablePaginationProps: variancePaginationProps } = useClientPaginatedRows(variance);

  const load = useCallback(() => {
    Promise.all([financeApi.listBudgets(year), financeApi.budgetVariance(year)])
      .then(([b, v]) => {
        setBudgets(b);
        setVariance(v);
      })
      .catch((e) => showApiError(e, 'Failed to load budgets'));
  }, [year]);

  useEffect(() => {
    load();
  }, [load]);

  const createBudget = async () => {
    if (!form.budgetCode.trim() || !form.budgetName.trim()) {
      notify.error('Budget code and name are required');
      return;
    }
    try {
      await financeApi.createBudget(form);
      notify.success('Budget created');
      setForm({ budgetCode: '', budgetName: '', fiscalYear: year });
      load();
    } catch (e) {
      showApiError(e, 'Create budget failed');
    }
  };

  return (
    <div>
      <PageHeader
        title="Budgets & variance"
        description="Annual and departmental budgets with live variance against actual GL"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'Finance', to: '/finance' }, { label: 'Budgets' }]}
      />

      <div className="card p-4 mb-4 grid gap-3 md:grid-cols-4">
        <Input
          placeholder="Budget code"
          value={form.budgetCode}
          onChange={(e) => setForm({ ...form, budgetCode: e.target.value.toUpperCase() })}
        />
        <Input
          placeholder="Budget name"
          value={form.budgetName}
          onChange={(e) => setForm({ ...form, budgetName: e.target.value })}
        />
        <Input
          type="number"
          value={form.fiscalYear}
          onChange={(e) => setForm({ ...form, fiscalYear: Number(e.target.value) })}
        />
        <Button onClick={() => void createBudget()}>Create budget</Button>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="card overflow-x-auto">
          <h3 className="font-semibold p-3 border-b">Budgets ({year})</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="p-2 text-left">Code</th>
                <th className="p-2 text-left">Name</th>
                <th className="p-2 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {paginatedBudgets.map((b) => (
                <tr key={b.id} className="border-b">
                  <td className="p-2 font-mono">{b.budgetCode}</td>
                  <td className="p-2">{b.budgetName}</td>
                  <td className="p-2">{b.statusCode}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <TablePagination {...budgetsPaginationProps} hasRows={budgets.length > 0} />
        </div>
        <div className="card overflow-x-auto">
          <h3 className="font-semibold p-3 border-b">Variance analysis</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="p-2 text-left">Account</th>
                <th className="p-2 text-right">Planned</th>
                <th className="p-2 text-right">Actual</th>
                <th className="p-2 text-right">Variance</th>
              </tr>
            </thead>
            <tbody>
              {paginatedVariance.map((row, i) => (
                <tr key={`${row.accountCode ?? row.accountId ?? i}`} className="border-b">
                  <td className="p-2">{row.accountCode ?? row.accountId ?? '—'}</td>
                  <td className="p-2 text-right">{row.planned?.toFixed(2)}</td>
                  <td className="p-2 text-right">{row.actual?.toFixed(2)}</td>
                  <td className="p-2 text-right">{row.variance?.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <TablePagination {...variancePaginationProps} hasRows={variance.length > 0} />
        </div>
      </div>
    </div>
  );
}
